import cn from 'classnames';
import dayjs from 'dayjs';
import isEmpty from 'lodash/isEmpty';
import { useRouter } from 'next/router';
import { useState, useEffect } from 'react';

/* Components */
import globalStyles from '../../../styles/pages/index.module.scss';
import CategoriesAside from '../../components/CategoriesAside/CategoriesAside';
import CategoriesBanner from '../../components/CategoriesBanner/CategoriesBanner';
import CategoriesSelect from '../../components/CategoriesSelect/CategoriesSelect';
import CustomMetaHead from '../../components/CustomMetaHead';
import DeliveryBanner from '../../components/DeliveryBanner/DeliveryBanner';
import FavoritesAside from '../../components/FavoritesAside/FavoritesAside';
import Footer from '../../components/Footer/Footer';
import Header from '../../components/Header/Header';
import Navigation from '../../components/Navigation/Navigation';
import ProductMenu from '../../components/ProductsMenu/ProductMenu';
import META_DATA from '../../constants/metaData';
import CartStore from '../../store/cart';
/* Controllers */
import CategoryStore from '../../store/categories';
import DeliveryMode from '../../store/deliveryMode';
import ProductsCollection from '../../store/productsCollection';
import SortBy from '../../store/productsSort';
import BANNER_SORT_OPTIONS from '../../utils/bannerOptions';
/* Styles */
import { getOrFetchFooterLinks } from '../../utils/footerLinks';
import { createBreadcrumbJson } from '../../utils/getBreadcrumb';
import { getOrFetchCategories } from '../../utils/getCategories';
import { getOrFetchNavigationLinks } from '../../utils/navigationLinks';
import { isOffersPage, isSecondLevelPage } from '../../utils/products';
import { serverSideBannersFetchingFromFile } from '../../utils/server-side/banners';
import { serverSideCategoryTreeFetchingFromFile } from '../../utils/server-side/categoryTree';
import { serverSideOffersFetchingFromFile } from '../../utils/server-side/offers';
import { serverSideFetchingFromFile } from '../../utils/server-side/products';
import { getCategoryPaths } from '../../utils/staticPaths';
import styles from '../products/products.module.scss';

export default function CategoriesProducts({
  banners,
  footerLinks,
  navigation,
  offers,
  canonical,
  category,
  subCategory,
  staticProducts,
  meta,
  categoriesFromFile,
}) {
  const router = useRouter();
  const [deliveryMode, setDeliveryMode] = useState('');
  const [numOfProducts, setNumOfProducts] = useState(0);
  const [showBySelection, setShowBySelection] = useState(BANNER_SORT_OPTIONS.catalog);
  const [isCartVisible, setIsCartVisible] = useState(false);
  const [productCollections, setProductCollections] = useState();
  const [categories, setCategories] = useState();
  const { slug } = router.query;
  const _isOffersPage = isOffersPage(slug);
  const _isSecondLevelPage = isSecondLevelPage(slug);
  const categoryName = category?.name;
  const subCategoryName = subCategory?.name;

  const breadcrumbData = [
    {
      url: `${process.env.domain}/categories/${slug[0]}/`,
      name: slug[0],
    },
    {
      url: `${process.env.domain}/${canonical}/`,
      name: slug[1],
    },
  ];
  const breadcrumb = createBreadcrumbJson(breadcrumbData);

  useEffect(() => {
    const subscriptionDeliveryMode = DeliveryMode.subscribe(setDeliveryMode);
    const productCollections$ = ProductsCollection.subscribe(setProductCollections);
    const isCartVisible$ = CartStore.getIsVisble$().subscribe(setIsCartVisible);
    const sortBy$ = SortBy.subscribe(setShowBySelection);
    const categories$ = CategoryStore.subscribe(setCategories);

    return () => {
      sortBy$.unsubscribe();
      isCartVisible$.unsubscribe();
      productCollections$.unsubscribe();
      subscriptionDeliveryMode.unsubscribe();
      categories$.unsubscribe();
    };
  }, []);

  return (
    <>
      {slug.length > 1 && (
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumb) }} />
      )}
      <CustomMetaHead meta={meta} />
      <div className={globalStyles.headerContainer}>
        <Header navigation={navigation} isEnabled />
      </div>

      <Navigation navigation={navigation} />

      <DeliveryBanner />

      <CategoriesSelect slug={slug} />

      <main
        className={cn(styles.main, {
          [styles.spaceOpenCart]: isCartVisible,
        })}
      >
        <div className={cn([styles.mainLeft, styles.listBanner])}>
          <div className={cn([styles.sticky])}>
            <CategoriesBanner
              onChangeOrderBy={e => SortBy.update(e.target.value)}
              {...{
                showBySelection,
                numOfProducts,
                categoryName,
                subCategoryName,
                slug,
              }}
            />
          </div>

          <section className={styles.mainContent}>
            <div className={styles.hideOnSmallScreen}>
              <div className={cn([styles.sticky, styles.categoriesAsideTop, styles.categoriesAsideProductDetails])}>
                <FavoritesAside />
                <CategoriesAside categoriesFromFile={categoriesFromFile} slug={slug} category={category} />
              </div>
            </div>
            <div className={styles.productScrollingContainer}>
              <ProductMenu
                isSecondLevelPage={_isSecondLevelPage}
                isOfferListPage={_isOffersPage}
                categories={categories}
                category={category}
                subCategory={subCategory}
                productCollections={productCollections}
                slug={slug}
                setNumOfProducts={setNumOfProducts}
                deliveryMode={deliveryMode}
                offers={offers}
                banners={banners}
                showBySelection={showBySelection}
                staticProducts={staticProducts}
              />
            </div>
          </section>
        </div>
      </main>

      <Footer {...{ footerLinks }} />
    </>
  );
}

export async function getStaticPaths() {
  const [categories] = await Promise.all([getOrFetchCategories()]);

  const allCategoryPaths = getCategoryPaths(categories);
  const paths = allCategoryPaths.map(path => {
    return {
      params: { slug: path },
    };
  });

  return { paths, fallback: false };
}

export async function getStaticProps(context) {
  const { slug } = context.params;
  const [{ data: navigation }, footerLinks, categories] = await Promise.all([
    getOrFetchNavigationLinks(),
    getOrFetchFooterLinks(),
    getOrFetchCategories(),
  ]);

  const nowUnix = dayjs().unix();
  const offersAll = serverSideOffersFetchingFromFile();
  const banners = serverSideBannersFetchingFromFile();
  const categoriesFromFile = serverSideCategoryTreeFetchingFromFile();

  const availableBanners = banners.filter(banner => banner.available);
  const offers = offersAll.filter(
    offer => offer.enabled &&
      offer.available &&
      offer.beginDate < nowUnix &&
      offer.endDate > nowUnix &&
      offer.offerType !== 'super'
  );
  const allStaticProducts = serverSideFetchingFromFile();
  function findProductsSubcategory(subcategory, _products) {
    if (subcategory.subCategories.length !== 0) {
      const productsInCategory = _products.filter(p => subcategory._id === p?.category?.categoryTree?.parent);
      return { [subcategory._id]: productsInCategory };
    }
    const productsInCategory = _products.filter(p => subcategory._id === p?.category?._id);
    return { [subcategory._id]: productsInCategory };
  }

  const category = categories?.find(c => c.slugAscii === slug[0]) || {};
  const subCategory = category.subCategories.find(c => c.slugAscii === slug[1]) || {};
  const bioPrefix = slug.includes('biologikaleitourgika') ? 'Bio - ' : '';

  const title = `${bioPrefix}${subCategory?.name || category.name}`;
  const description = `${bioPrefix}${title}: Βρείτε όλα τα προϊόντα & τις προσφορές της κατηγορίας.
  Αγοράστε online σήμερα με ένα κλικ τα προϊόντα που θέλετε!`;

  const staticProducts = isEmpty(subCategory)
    ? category.subCategories.reduce((allProducts, _category) => {
      const productsInCategory = findProductsSubcategory(_category, allStaticProducts);
      return { ...allProducts, ...productsInCategory };
    }, [])
    : findProductsSubcategory(subCategory, allStaticProducts);

  const meta = {
    ...META_DATA[process.env.appName].categories,
    title,
    description,
    url: `${process.env.domain}/categories/${slug.join('/')}/`,
  };

  return {
    props: {
      banners: availableBanners,
      footerLinks,
      navigation,
      offers,
      canonical: `categories/${slug.join('/')}`,
      category,
      subCategory,
      staticProducts,
      meta,
      categoriesFromFile,
    },
  };
}
